import { FC, useEffect } from 'react';
import { isRouteErrorResponse, useLocation, useRouteError } from 'react-router-dom';
import { datadogLogs } from '@datadog/browser-logs';
import { NotFoundScreen } from 'screens/NotFound';
import { getPathWithRef } from 'helpers/utils';
import { noteBoundaryCaughtError } from 'helpers/boundaryCaughtErrors';
import { reportReactError } from 'helpers/reactErrorReporting';

export const RouteErrorElement: FC = () => {
  const error = useRouteError();
  const { pathname } = useLocation();
  // Unmatched URLs land here as 404s; those aren't crashes.
  const isNotFound = isRouteErrorResponse(error) && error.status === 404;
  noteBoundaryCaughtError(error);

  useEffect(() => {
    if (isNotFound) return;
    const reportedError = error instanceof Error ? error : new Error(String(error));
    datadogLogs.logger.warn('Unhandled route error', { path: pathname }, reportedError);
    reportReactError(reportedError);
  }, [error, isNotFound, pathname]);

  if (isNotFound) return <NotFoundScreen />;

  // Static on purpose: no boundary sits above this one. A full page load discards the crashed state.
  return (
    <div className="route-error">
      <span className="h3-dtm">Something went wrong</span>
      <span className="body-text">Please try again, or head back to the homepage.</span>
      <a className="button-brand" href={getPathWithRef('/')}>
        Go home
      </a>
    </div>
  );
};
